import { useQuery } from '@tanstack/react-query'
import { Search, Users } from 'lucide-react'
import { useState } from 'react'

import { getApiGuestsOptions } from '@/lib/api/client/@tanstack/react-query.gen'
import { useFieldContext } from '@/lib/forms/context'
import { cn } from '@/lib/utils'

import { Field, FieldContent, FieldError, FieldLabel } from '../ui/field'
import { Input } from '../ui/input'

type Props = {
  label: string
}

export const MasterGuestSelectField = ({ label }: Props) => {
  const field = useFieldContext<string>()
  const [searchTerm, setSearchTerm] = useState('')

  const { data: guestsData, isLoading } = useQuery(
    getApiGuestsOptions({ query: { q: searchTerm, limit: 20 } }),
  )

  const guests = guestsData?.data || []
  const selectedId = field.state.value

  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

  return (
    <Field data-invalid={isInvalid}>
      <FieldContent>
        <FieldLabel className="text-foreground text-base font-bold">
          {label}
        </FieldLabel>
      </FieldContent>

      <div className="border-muted flex flex-col gap-3 rounded-2xl border bg-white p-4">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            placeholder="ค้นหาชื่อแขกใน Master List..."
            className="h-10 pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="max-h-60 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="text-muted-foreground py-4 text-center text-sm">
              กำลังโหลด...
            </div>
          ) : guests.length === 0 ? (
            <div className="text-muted-foreground py-4 text-center text-sm">
              ไม่พบข้อมูล
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {guests.map((guest: any) => {
                const isSelected = selectedId === guest.id
                return (
                  <label
                    key={guest.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 rounded-xl p-3 transition-colors',
                      isSelected
                        ? 'bg-primary/5 border-primary/20 border'
                        : 'hover:bg-muted/50 border border-transparent',
                    )}
                  >
                    <div className="relative flex items-center justify-center">
                      <input
                        type="radio"
                        name={field.name}
                        className="text-primary size-4"
                        checked={isSelected}
                        onChange={() => field.handleChange(guest.id)}
                      />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <span className="text-sm font-bold">
                        {guest.fullName}
                      </span>
                      <div className="flex items-center gap-2">
                        {guest.group && (
                          <span className="text-muted-foreground text-[10px]">
                            {guest.group}
                          </span>
                        )}
                        <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
                          <Users className="size-3" />
                          {guest.expectedFollowers} ท่าน
                        </span>
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {isInvalid && <FieldError errors={field.state.meta.errors} />}
    </Field>
  )
}
